import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { DailyWorkJoin } from "@/repositories/daily-work.repository";
import {
  DailyWorkScheduleRowJoin,
  SaveScheduleAssignmentInput,
  SaveScheduleRowInput,
} from "@/repositories/daily-work-schedule.repository";
import { day_of_week } from "@/models/daily-work.model";
import {
  MonthlyScheduleRule,
  monthly_schedule_occurrence,
  schedule_assignment_kind,
} from "@/models/daily-work-schedule.model";
import type { Service } from "@shyne/shared/api";
import {
  createLocalId,
  findDuplicateAssignmentSubjects,
  parseMaxHoursInput,
  dayOfWeekLabels,
  dayToIndex,
  indexToDay,
  monthlyOccurrenceLabels,
  orderedWeekdays,
} from "@/lib/daily-work/master-schedule";

const orderedOccurrences: monthly_schedule_occurrence[] = ["first", "second", "third", "fourth", "last"];

export interface AssignmentOption {
  id: string;
  name: string;
}

interface StaffingDraft {
  localId: string;
  id?: string;
  assignment_kind: schedule_assignment_kind;
  assigned_user: string | null;
  assigned_org: string | null;
  quantity: number;
  rate_override: string;
}

interface MasterScheduleRowDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contracts: DailyWorkJoin[];
  services: Service[];
  individuals: AssignmentOption[];
  subs: AssignmentOption[];
  /** `user:<id>:<serviceId>` / `org:<id>:<serviceId>` -> hourly rate. */
  rateMap: Record<string, number>;
  row?: DailyWorkScheduleRowJoin | null;
  templateRow?: DailyWorkScheduleRowJoin | null;
  defaultWeekday?: day_of_week;
  defaultDailyWorkId?: string | null;
  onSave: (payload: SaveScheduleRowInput) => Promise<boolean>;
}

function tasksToText(tasks: string[]) {
  return tasks.join("\n");
}

function textToTasks(value: string) {
  return value
    .split(/\r?\n/)
    .map((task) => task.trim())
    .filter(Boolean);
}

export default function MasterScheduleRowDialog({
  open,
  onOpenChange,
  contracts,
  services,
  individuals,
  subs,
  rateMap,
  row,
  templateRow,
  defaultWeekday = "monday",
  defaultDailyWorkId = null,
  onSave,
}: MasterScheduleRowDialogProps) {
  const sourceRow = row ?? templateRow ?? null;
  const isEditing = Boolean(row);
  const [saving, setSaving] = useState(false);
  const [dailyWorkId, setDailyWorkId] = useState("");
  const [weekday, setWeekday] = useState<day_of_week>(defaultWeekday);
  const [selectedWeekdays, setSelectedWeekdays] = useState<day_of_week[]>([defaultWeekday]);
  const [serviceId, setServiceId] = useState("");
  const [durationLabel, setDurationLabel] = useState("");
  const [maxHours, setMaxHours] = useState("");
  const [instructions, setInstructions] = useState("");
  const [weeklyTasksText, setWeeklyTasksText] = useState("");
  const [staffing, setStaffing] = useState<StaffingDraft[]>([]);
  const [monthlyRules, setMonthlyRules] = useState<Array<MonthlyScheduleRule & { tasks_text: string }>>([]);

  useEffect(() => {
    if (!open) return;

    const sourceWeekday = sourceRow ? indexToDay[sourceRow.weekday] : defaultWeekday;
    setDailyWorkId(sourceRow?.daily_work ?? defaultDailyWorkId ?? contracts[0]?.id ?? "");
    setWeekday(sourceWeekday);
    setSelectedWeekdays([sourceWeekday]);
    setServiceId(sourceRow?.service ?? "");
    setDurationLabel(sourceRow?.duration_label ?? "");
    setMaxHours(sourceRow?.max_hours != null ? String(sourceRow.max_hours) : "");
    setInstructions(sourceRow?.instructions ?? "");
    setWeeklyTasksText(tasksToText(sourceRow?.weekly_tasks ?? []));
    setStaffing(
      sourceRow?.assignments?.map((assignment) => ({
        localId: createLocalId("allocation"),
        // Duplicating a row must create fresh assignments, not adopt the source's.
        id: row ? assignment.id : undefined,
        assignment_kind: assignment.assignment_kind,
        assigned_user: assignment.assigned_user,
        assigned_org: assignment.assigned_org,
        quantity: assignment.quantity || 1,
        rate_override: assignment.rate_override != null ? String(assignment.rate_override) : "",
      })) ?? []
    );
    setMonthlyRules(
      sourceRow?.monthly_rules?.map((rule) => ({
        ...rule,
        id: row ? rule.id : createLocalId("monthly"),
        tasks_text: tasksToText(rule.tasks),
      })) ?? []
    );
  }, [open, sourceRow, row, defaultDailyWorkId, defaultWeekday, contracts]);

  const addStaffing = () => {
    setStaffing((current) => [
      ...current,
      {
        localId: createLocalId("allocation"),
        assignment_kind: "individual",
        assigned_user: null,
        assigned_org: null,
        quantity: 1,
        rate_override: "",
      },
    ]);
  };

  const addMonthlyRule = () => {
    setMonthlyRules((current) => [
      ...current,
      {
        id: createLocalId("monthly"),
        occurrence: "first",
        tasks: [],
        tasks_text: "",
      },
    ]);
  };

  const updateStaffing = (localId: string, updater: (current: StaffingDraft) => StaffingDraft) => {
    setStaffing((current) => current.map((draft) => (draft.localId === localId ? updater(draft) : draft)));
  };

  const updateMonthlyRule = (
    id: string,
    updater: (current: MonthlyScheduleRule & { tasks_text: string }) => MonthlyScheduleRule & { tasks_text: string }
  ) => {
    setMonthlyRules((current) => current.map((rule) => (rule.id === id ? updater(rule) : rule)));
  };

  const profileRateFor = (draft: StaffingDraft): number | null => {
    if (!serviceId) return null;
    if (draft.assignment_kind === "company") {
      return draft.assigned_org != null ? rateMap[`org:${draft.assigned_org}:${serviceId}`] ?? null : null;
    }
    return draft.assigned_user != null ? rateMap[`user:${draft.assigned_user}:${serviceId}`] ?? null : null;
  };

  const handleSave = async () => {
    if (!dailyWorkId) {
      toast.error("Choose a property contract");
      return;
    }
    if (!serviceId) {
      toast.error("Choose a service");
      return;
    }

    const weekdaysToSave = isEditing ? [weekday] : selectedWeekdays;
    if (!weekdaysToSave.length) {
      toast.error("Choose at least one weekday");
      return;
    }

    const maxHoursResult = parseMaxHoursInput(maxHours);
    if (maxHoursResult.ok === false) {
      toast.error(maxHoursResult.error);
      return;
    }

    const pickedStaffing = staffing.filter((draft) => draft.assigned_user || draft.assigned_org);
    if (findDuplicateAssignmentSubjects(pickedStaffing).length > 0) {
      toast.error("Each person or company can only be listed once per row — remove the duplicate staffing entry");
      return;
    }

    const assignments: SaveScheduleAssignmentInput[] = pickedStaffing
      .map((draft) => ({
        id: draft.id,
        assignment_kind: draft.assignment_kind,
        assigned_user: draft.assignment_kind === "individual" ? draft.assigned_user : null,
        assigned_org: draft.assignment_kind === "company" ? draft.assigned_org : null,
        quantity: Math.max(1, Number(draft.quantity || 1)),
        rate_override:
          draft.rate_override.trim() !== "" && Number.isFinite(Number(draft.rate_override))
            ? Number(draft.rate_override)
            : null,
      }));

    const basePayload = {
      daily_work: dailyWorkId,
      service: serviceId,
      duration_label: durationLabel.trim() || null,
      max_hours: maxHoursResult.value,
      instructions: instructions.trim() || null,
      weekly_tasks: textToTasks(weeklyTasksText),
      monthly_rules: monthlyRules
        .map((rule) => ({
          id: rule.id,
          occurrence: rule.occurrence,
          tasks: textToTasks(rule.tasks_text),
        }))
        .filter((rule) => rule.tasks.length > 0),
      assignments,
    };

    const payload: SaveScheduleRowInput = {
      ...basePayload,
      ...(isEditing ? { id: row?.id } : {}),
      weekdays: weekdaysToSave.map((selectedDay) => dayToIndex[selectedDay]),
    };

    setSaving(true);
    try {
      const saved = await onSave(payload);
      if (saved) onOpenChange(false);
    } catch (error: any) {
      // onSave reports its own toasts; this only prevents an unhandled rejection.
      console.error("Schedule row save failed:", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[min(96vw,1280px)] max-w-none sm:max-w-none overflow-hidden p-0">
        <div className="max-h-[90vh] overflow-y-auto">
          <DialogHeader className="border-b px-6 py-5">
            <DialogTitle>
              {isEditing ? "Edit Schedule Row" : templateRow ? "Duplicate Schedule Row" : "Add Schedule Row"}
            </DialogTitle>
            <DialogDescription className="max-w-2xl">
              Define the recurring shift, staffing, checklist, and monthly reminder rules. Saving updates the upcoming
              live schedule automatically.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 px-6 py-5">
            <section className="space-y-4 rounded-xl border bg-muted/20 p-4">
              <div className="space-y-1">
                <h3 className="text-sm font-semibold">Shift Details</h3>
                <p className="text-sm text-muted-foreground">
                  {isEditing
                    ? "Adjust the weekday, service, and the basic work expectations for this recurring row."
                    : "Set the property contract, service, and the basic work expectations for this recurring row, then pick the weekdays it applies to."}
                </p>
              </div>

              <div className="grid gap-4 xl:grid-cols-12">
                <div className={`min-w-0 space-y-2 ${isEditing ? "xl:col-span-4" : "xl:col-span-6"}`}>
                  <Label>Property Contract</Label>
                  {/* A row's generated days are keyed to its contract; moving a row would
                      strand them. To move, duplicate the row onto the other contract and
                      delete this one. */}
                  <Select value={dailyWorkId} onValueChange={setDailyWorkId} disabled={isEditing}>
                    <SelectTrigger className="w-full bg-background">
                      <SelectValue placeholder="Choose property contract" />
                    </SelectTrigger>
                    <SelectContent>
                      {contracts.map((contract) => (
                        <SelectItem key={contract.id} value={contract.id}>
                          {contract.property.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {isEditing && (
                    <p className="text-xs text-muted-foreground">
                      To move this row to another contract, duplicate it there and delete this one.
                    </p>
                  )}
                </div>

                {isEditing && (
                  <div className="min-w-0 space-y-2 xl:col-span-2">
                    <Label>Weekday</Label>
                    <Select value={weekday} onValueChange={(value) => setWeekday(value as day_of_week)}>
                      <SelectTrigger className="w-full bg-background">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {orderedWeekdays.map((day) => (
                          <SelectItem key={day} value={day}>
                            {dayOfWeekLabels[day]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="min-w-0 space-y-2 xl:col-span-4">
                  <Label>Service</Label>
                  <Select value={serviceId} onValueChange={setServiceId}>
                    <SelectTrigger className="w-full bg-background">
                      <SelectValue placeholder="Choose service" />
                    </SelectTrigger>
                    <SelectContent>
                      {services.map((service) => (
                        <SelectItem key={service.id} value={service.id}>
                          {service.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="min-w-0 space-y-2 xl:col-span-2">
                  <Label>Max Hours</Label>
                  <Input
                    className="bg-background"
                    type="number"
                    min="0.25"
                    step="0.25"
                    value={maxHours}
                    onChange={(event) => setMaxHours(event.target.value)}
                    placeholder="8"
                  />
                </div>
              </div>

              {!isEditing && (
                <div className="space-y-3 rounded-lg border bg-background p-3">
                  <div className="space-y-1">
                    <Label>Apply To Weekdays</Label>
                    <p className="text-sm text-muted-foreground">
                      One matching recurring row is created for each checked weekday.
                    </p>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
                    {orderedWeekdays.map((day) => {
                      const checked = selectedWeekdays.includes(day);
                      return (
                        <label
                          key={day}
                          className="flex items-center gap-3 rounded-md border px-3 py-2 text-sm cursor-pointer hover:bg-muted/40"
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(nextChecked) =>
                              setSelectedWeekdays((current) =>
                                nextChecked
                                  ? [...current, day]
                                  : current.filter((selectedDay) => selectedDay !== day)
                              )
                            }
                          />
                          <span>{dayOfWeekLabels[day]}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="grid gap-4 xl:grid-cols-[minmax(0,1.15fr),minmax(0,1fr)]">
                <div className="min-w-0 space-y-2">
                  <Label>Duration Label</Label>
                  <Input
                    className="bg-background"
                    value={durationLabel}
                    onChange={(event) => setDurationLabel(event.target.value)}
                    placeholder="8 hrs/day or morning and afternoon"
                  />
                </div>

                <div className="min-w-0 space-y-2">
                  <Label>Instructions</Label>
                  <Textarea
                    className="min-h-[88px] bg-background"
                    value={instructions}
                    onChange={(event) => setInstructions(event.target.value)}
                    placeholder="Optional reminders, route notes, or scope details"
                  />
                </div>
              </div>
            </section>

            <section className="space-y-3 rounded-xl border p-4">
              <div className="space-y-1">
                <h3 className="text-sm font-semibold">Weekly Checklist</h3>
                <p className="text-sm text-muted-foreground">
                  Enter one task per line. These tasks are included every day this row generates.
                </p>
              </div>
              <Textarea
                rows={5}
                value={weeklyTasksText}
                onChange={(event) => setWeeklyTasksText(event.target.value)}
                placeholder={"Enter one task per line\nTrash out common areas\nCheck lobby glass"}
              />
            </section>

            <section className="space-y-4 rounded-xl border p-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h3 className="font-medium">Staffing</h3>
                  <p className="text-sm text-muted-foreground">
                    Add individuals or subcontractor companies. Each entry creates its own daily slot.
                  </p>
                </div>
                <Button type="button" variant="outline" onClick={addStaffing}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Staffing
                </Button>
              </div>

              {staffing.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No assignee added yet. The schedule will generate one unassigned slot per day.
                </p>
              ) : (
                staffing.map((draft, index) => {
                  const options = draft.assignment_kind === "company" ? subs : individuals;
                  const profileRate = profileRateFor(draft);
                  const overrideNumber =
                    draft.rate_override.trim() !== "" && Number.isFinite(Number(draft.rate_override))
                      ? Number(draft.rate_override)
                      : null;

                  return (
                    <div key={draft.localId} className="rounded-lg border bg-muted/20 p-4 space-y-4">
                      <div className="flex items-center justify-between gap-4">
                        <Badge variant="secondary">Staffing {index + 1}</Badge>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            setStaffing((current) => current.filter((item) => item.localId !== draft.localId))
                          }
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>

                      <div className="grid gap-4 lg:grid-cols-12">
                        <div className="min-w-0 space-y-2 lg:col-span-3">
                          <Label>Assignee Type</Label>
                          <Select
                            value={draft.assignment_kind}
                            onValueChange={(value) =>
                              updateStaffing(draft.localId, (current) => ({
                                ...current,
                                assignment_kind: value as schedule_assignment_kind,
                                assigned_user: null,
                                assigned_org: null,
                                quantity: 1,
                              }))
                            }
                          >
                            <SelectTrigger className="w-full bg-background">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="individual">Individual</SelectItem>
                              <SelectItem value="company">Company</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div
                          className={`min-w-0 space-y-2 ${draft.assignment_kind === "company" ? "lg:col-span-5" : "lg:col-span-9"}`}
                        >
                          <Label>{draft.assignment_kind === "company" ? "Company" : "Individual"}</Label>
                          <Select
                            value={
                              draft.assignment_kind === "company"
                                ? draft.assigned_org ?? "__empty__"
                                : draft.assigned_user ?? "__empty__"
                            }
                            onValueChange={(value) =>
                              updateStaffing(draft.localId, (current) => ({
                                ...current,
                                assigned_user:
                                  current.assignment_kind === "company" ? null : value === "__empty__" ? null : value,
                                assigned_org:
                                  current.assignment_kind === "company" ? (value === "__empty__" ? null : value) : null,
                              }))
                            }
                          >
                            <SelectTrigger className="w-full bg-background">
                              <SelectValue placeholder="Choose assignee" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__empty__">Unassigned</SelectItem>
                              {options.map((option) => (
                                <SelectItem key={option.id} value={option.id}>
                                  {option.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {draft.assignment_kind === "company" && (
                          <div className="min-w-0 space-y-2 lg:col-span-4">
                            <Label>People needed from this company</Label>
                            <Input
                              className="bg-background"
                              type="number"
                              min="1"
                              step="1"
                              value={draft.quantity}
                              onChange={(event) =>
                                updateStaffing(draft.localId, (current) => ({
                                  ...current,
                                  quantity: Math.max(1, Number(event.target.value || 1)),
                                }))
                              }
                            />
                            <p className="text-xs text-muted-foreground">
                              Creates that many separate slots the company assigns to its team members.
                            </p>
                          </div>
                        )}
                      </div>

                      <div className="grid gap-4 lg:grid-cols-2">
                        <div className="min-w-0 space-y-2">
                          <Label>Override Rate (optional)</Label>
                          <Input
                            className="bg-background"
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder={profileRate != null ? `Profile rate $${profileRate.toFixed(2)}` : "No profile rate set"}
                            value={draft.rate_override}
                            onChange={(event) =>
                              updateStaffing(draft.localId, (current) => ({
                                ...current,
                                rate_override: event.target.value,
                              }))
                            }
                          />
                        </div>

                        <div className="min-w-0 space-y-2">
                          <Label>Effective Hourly Rate</Label>
                          <div className="flex min-h-10 items-center rounded-md border bg-background px-3 text-sm text-muted-foreground">
                            {overrideNumber != null
                              ? `$${overrideNumber.toFixed(2)} override`
                              : profileRate != null
                                ? `$${profileRate.toFixed(2)} from profile`
                                : "No rate configured — set one on the assignee profile"}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </section>

            <section className="space-y-4 rounded-xl border p-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h3 className="font-medium">Monthly Reminder Rules</h3>
                  <p className="text-sm text-muted-foreground">
                    Add month-specific tasks like first-Monday or last-Friday reminders for this recurring row.
                  </p>
                </div>
                <Button type="button" variant="outline" onClick={addMonthlyRule}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Monthly Rule
                </Button>
              </div>

              {monthlyRules.length === 0 ? (
                <p className="text-sm text-muted-foreground">No monthly reminder rules configured.</p>
              ) : (
                monthlyRules.map((rule, index) => (
                  <div key={rule.id} className="rounded-lg border bg-muted/20 p-4 space-y-4">
                    <div className="flex items-center justify-between gap-4">
                      <Badge variant="secondary">Monthly Rule {index + 1}</Badge>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setMonthlyRules((current) => current.filter((item) => item.id !== rule.id))}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>

                    <div className="grid gap-4 lg:grid-cols-[220px,minmax(0,1fr)]">
                      <div className="min-w-0 space-y-2">
                        <Label>Occurrence</Label>
                        <Select
                          value={rule.occurrence}
                          onValueChange={(value) =>
                            updateMonthlyRule(rule.id, (current) => ({
                              ...current,
                              occurrence: value as monthly_schedule_occurrence,
                            }))
                          }
                        >
                          <SelectTrigger className="w-full bg-background">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {orderedOccurrences.map((occurrence) => (
                              <SelectItem key={occurrence} value={occurrence}>
                                {monthlyOccurrenceLabels[occurrence]}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="min-w-0 space-y-2">
                        <Label>Tasks</Label>
                        <Textarea
                          className="bg-background"
                          rows={4}
                          value={rule.tasks_text}
                          onChange={(event) =>
                            updateMonthlyRule(rule.id, (current) => ({
                              ...current,
                              tasks_text: event.target.value,
                            }))
                          }
                          placeholder={"Enter one task per line\nHigh dust hallway six"}
                        />
                      </div>
                    </div>
                  </div>
                ))
              )}
            </section>
          </div>

          <DialogFooter className="border-t px-6 py-4">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving
                ? "Saving..."
                : isEditing
                  ? "Save Row"
                  : selectedWeekdays.length > 1
                    ? `Add ${selectedWeekdays.length} Rows`
                    : "Add Row"}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
